import { useEffect, useRef, useState } from "react";

/**
 * Simple pan + zoom photo framer for VOIHU messages. Preserves aspect ratio.
 *
 * Props:
 *   src            — original photo URL
 *   initial        — { x:0, y:0, scale:1 } (or the current saved framing)
 *   aspect         — CSS aspect-ratio for the frame (default "16 / 9")
 *   onSave(frame)  — invoked with the new { x, y, scale }
 *   onCancel()     — invoked when the user cancels the edit
 */
export default function PhotoEditor({ src, initial, aspect = "16 / 9",
                                       onSave, onCancel }) {
  const [frame, setFrame] = useState(initial || { x: 0, y: 0, scale: 1 });
  const frameRef = useRef(null);
  const dragRef = useRef(null); // {pointerId, startX, startY, startFx, startFy}
  const pinchRef = useRef(null); // {startDist, startScale}

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  const setScale = (next) =>
    setFrame(f => ({ ...f, scale: clamp(next, 1, 3) }));

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { pointerId: e.pointerId, startX: e.clientX,
                        startY: e.clientY, startFx: frame.x, startFy: frame.y };
  };

  const onPointerMove = (e) => {
    if (!dragRef.current || dragRef.current.pointerId !== e.pointerId) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setFrame(f => ({ ...f, x: dragRef.current.startFx + dx,
                          y: dragRef.current.startFy + dy }));
  };

  const onPointerUp = (e) => {
    if (dragRef.current && dragRef.current.pointerId === e.pointerId) {
      dragRef.current = null;
    }
  };

  const onWheel = (e) => {
    e.preventDefault();
    setScale(frame.scale + (e.deltaY < 0 ? 0.1 : -0.1));
  };

  // Two-finger pinch (touch)
  const touchesRef = useRef({});
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const onTouchStart = (ev) => {
      Array.from(ev.touches).forEach(t => { touchesRef.current[t.identifier] =
        { x: t.clientX, y: t.clientY }; });
      const ids = Object.keys(touchesRef.current);
      if (ids.length === 2) {
        const [a, b] = ids.map(k => touchesRef.current[k]);
        pinchRef.current = { startDist: dist(a, b), startScale: frame.scale };
      }
    };
    const onTouchMove = (ev) => {
      if (ev.touches.length === 2 && pinchRef.current) {
        ev.preventDefault();
        const [a, b] = Array.from(ev.touches);
        const d = dist({ x: a.clientX, y: a.clientY },
                       { x: b.clientX, y: b.clientY });
        setScale(pinchRef.current.startScale * (d / pinchRef.current.startDist));
      }
    };
    const onTouchEnd = () => { pinchRef.current = null; touchesRef.current = {}; };
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [frame.scale]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="photo-editor">
      <div className="w-full max-w-[560px] bg-white rounded-none shadow-xl">
        <div className="px-5 pt-5 pb-3 oh-label">EDIT PHOTO · drag · pinch or wheel to zoom</div>
        <div
          ref={frameRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
          className="relative w-full overflow-hidden touch-none select-none cursor-grab active:cursor-grabbing bg-neutral-100"
          style={{ aspectRatio: aspect }}
          data-testid="photo-editor-frame"
        >
          <img
            src={src}
            alt=""
            draggable={false}
            className="absolute top-1/2 left-1/2 max-w-none pointer-events-none"
            style={{
              transform: `translate(-50%, -50%) translate(${frame.x}px, ${frame.y}px) scale(${frame.scale})`,
              width: "100%", height: "100%", objectFit: "cover",
            }}
          />
        </div>
        <div className="px-5 py-3 flex items-center gap-3 oh-label">
          <span>ZOOM</span>
          <input
            type="range" min="1" max="3" step="0.01"
            value={frame.scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
            className="flex-1"
            data-testid="photo-editor-zoom"
          />
          <button type="button" onClick={() => setFrame({ x: 0, y: 0, scale: 1 })}
                  className="px-2 py-1 border border-black/20"
                  data-testid="photo-editor-reset">RESET</button>
        </div>
        <div className="px-5 pb-5 flex justify-between gap-3">
          <button type="button" onClick={onCancel}
                  className="oh-btn-ghost" data-testid="photo-editor-cancel">
            CANCEL
          </button>
          <button type="button" onClick={() => onSave(frame)}
                  className="oh-btn-primary" data-testid="photo-editor-save">
            SAVE FRAMING
          </button>
        </div>
      </div>
    </div>
  );
}
